import React from 'react';
import {ClickableElement} from '../../search/search-results/search-result-details/content/clickable-section-list/clickableSectionList';
import {spanToBoxPct} from './spanMath';

// Breathing room above and left of a content jump target, as a fraction of the visible box so it scales
// pane at every zoom. Flush against the edge is exact but reads as cramped. 10% is a taste call.
const CONTENT_JUMP_HEADROOM_FRACTION = 0.1;

/**
 * Scrolls a TOC or document-section jump target into view on both axes.
 *
 * Positioning reuses `spanMath.spanToBoxPct`, the same function the highlight overlay uses, so the two
 * paths cannot drift apart. It already handles the bottom-left origin and all four rotations.
 *
 * Both axes matter. At 90/270 the heading's position along the page is horizontal, so a vertical-only
 * scroll cannot reach it.
 *
 * Percentages apply to the rendered page box, not the scroll box, whose height is fixed. Deltas from
 * on-screen rects, so the maths survives a react-zoom-pan-pinch transform.
 */
export function scrollToElementFromContent(
    scrollTarget: ClickableElement,
    scrollElementReference: React.RefObject<any>,
    rotate: number,
): void {
    const hibuPdfPreviewPageScroll: HTMLDivElement | undefined = scrollElementReference.current ?? undefined;
    if (!hibuPdfPreviewPageScroll) {
        return;
    }
    // Absent until pdf.js has laid the page out; the `onRendered` pass calls us again once it is there.
    const pdfPage = hibuPdfPreviewPageScroll.querySelector('.pdfPage');
    if (!pdfPage) {
        return;
    }
    const scrollBoxCoordinates = hibuPdfPreviewPageScroll.getBoundingClientRect();
    const pageCoordinates = pdfPage.getBoundingClientRect();
    const targetBox = spanToBoxPct(scrollTarget.span, rotate);

    const targetScreenY = pageCoordinates.top + (pageCoordinates.height * targetBox.topPct) / 100;
    const targetScreenX = pageCoordinates.left + (pageCoordinates.width * targetBox.leftPct) / 100;
    const deltaY =
        targetScreenY - scrollBoxCoordinates.top - scrollBoxCoordinates.height * CONTENT_JUMP_HEADROOM_FRACTION;
    const deltaX =
        targetScreenX - scrollBoxCoordinates.left - scrollBoxCoordinates.width * CONTENT_JUMP_HEADROOM_FRACTION;

    // A malformed span would otherwise reach scrollBy as NaN and silently jump the view to 0.
    if (!Number.isFinite(deltaY) || !Number.isFinite(deltaX)) {
        return;
    }
    hibuPdfPreviewPageScroll.scrollBy({top: deltaY, left: deltaX});
}

/**
 * Scrolls a tag or query highlight into view on both axes.
 *
 * Horizontal matters once zoomed past fit-to-width, where the page is wider than the container.
 * `overflow-x: hidden` from scrollbox-mixin blocks the scrollbar and user scrolling, not programmatic
 * scrolling.
 *
 * Deltas rather than absolute offsets, so the maths survives a leftover react-zoom-pan-pinch translate.
 */
export function scrollToElementFromHighlight(htmlElement: Element, scrollElementReference: React.RefObject<any>): void {
    const hibuPdfPreviewPageScroll: HTMLDivElement | undefined = scrollElementReference.current ?? undefined;
    if (!hibuPdfPreviewPageScroll) {
        return;
    }
    const canvasCoordinates = hibuPdfPreviewPageScroll.getBoundingClientRect();
    const elemCoordinates = htmlElement.getBoundingClientRect();

    // centre the highlight in the visible box
    const deltaY =
        elemCoordinates.top -
        canvasCoordinates.top -
        (hibuPdfPreviewPageScroll.clientHeight - elemCoordinates.height) / 2;
    const deltaX =
        elemCoordinates.left -
        canvasCoordinates.left -
        (hibuPdfPreviewPageScroll.clientWidth - elemCoordinates.width) / 2;

    hibuPdfPreviewPageScroll.scrollBy({top: deltaY, left: deltaX});
}

// One pending removal per element, so re-selecting renews the outline. Otherwise a second click 900ms
// after the first is wiped 100ms later by the first timer, which looks like un-highlighting.
const pendingHighlightRemovals = new WeakMap<Element, ReturnType<typeof setTimeout>>();

export function highlightElement(element: Element): void {
    const pending = pendingHighlightRemovals.get(element);
    if (pending !== undefined) {
        clearTimeout(pending);
    }
    element.classList.add('current-hl');
    pendingHighlightRemovals.set(
        element,
        setTimeout(() => {
            pendingHighlightRemovals.delete(element);
            element.classList.remove('current-hl');
        }, 1000),
    );
}
